import { useEffect, useRef, useState } from 'react'
import { MapFilters, SpotlightVenue } from '../../../types/liveMap'
import { computePinCounts } from '../../../utils/liveMap/computePinCounts'
import { spotlightToNearby } from '../../../hooks/useSpotlightVenues'
import MarkerToken from './MarkerToken'

// Keep in sync with .map-venue-card width + margin-right in main.scss.
const CARD_WIDTH = 220
const CARD_GAP = 12
const SCROLL_SPEED_PX_PER_SEC = 50

/**
 * Horizontal scroll of spotlight cards along the bottom of the map, sourced from
 * GET user/venues/spotlighted (server-ranked, sized by the admin Spotlight
 * settings). Each card shows the same logo + count-badge token as the map
 * markers (and the mobile ticker), plus the server's detail line (a
 * "🔥 N recent viewers" flame, an active special, or direction/distance).
 * Tapping a card pans/zooms the map to the venue and opens its detail sheet.
 */

interface BottomVenueCardsProps {
  venues: SpotlightVenue[]
  filters: MapFilters
  activeVenueId?: string | null
  onSelect: (venue: SpotlightVenue) => void
}

function subtitleFor(venue: SpotlightVenue): string {
  const views = venue.peeks ?? venue.interestedUsers ?? 0
  if (views > 0) return `🔥 ${views} recent viewer${views === 1 ? '' : 's'}`
  return venue.details ?? ''
}

const BottomVenueCards = ({ venues, filters, activeVenueId, onSelect }: BottomVenueCardsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  // Track the available width so we only scroll when the cards don't fit.
  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  if (venues.length === 0) return null

  const singleSetWidth = venues.length * CARD_WIDTH + Math.max(0, venues.length - 1) * CARD_GAP
  const overflowing = containerWidth > 0 && singleSetWidth > containerWidth

  // Only duplicate (for a seamless loop) when scrolling is needed.
  const looped = overflowing ? [...venues, ...venues] : venues
  const durationSeconds = Math.max(15, (singleSetWidth + CARD_GAP) / SCROLL_SPEED_PX_PER_SEC)

  return (
    <div className="map-bottom-cards" ref={containerRef}>
      <div
        className={'map-bottom-cards__track' + (overflowing ? ' map-bottom-cards__track--scrolling' : '')}
        style={overflowing ? { animationDuration: `${durationSeconds}s` } : undefined}
      >
        {looped.map((venue, index) => {
          const counts = computePinCounts(spotlightToNearby(venue), filters)
          const isClone = index >= venues.length
          return (
            <button
              type="button"
              key={`${venue.publicId}-${index}`}
              className={
                'map-venue-card' +
                (venue.publicId === activeVenueId ? ' map-venue-card--active' : '')
              }
              aria-hidden={isClone || undefined}
              tabIndex={isClone ? -1 : undefined}
              onClick={() => onSelect(venue)}
            >
              <MarkerToken name={venue.name} logoUrl={venue.logoUrl} counts={counts} />
              <span className="map-venue-card__body">
                <span className="map-venue-card__name" title={venue.name}>
                  {venue.name}
                </span>
                <span className="map-venue-card__flame">{subtitleFor(venue)}</span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default BottomVenueCards
